import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { MapContext } from 'os-elements';
import { unByKey } from 'ol/Observable';
import { mapModes } from 'client/utils/constants/appStates';
import { LINE } from 'client/utils/constants/symbolName';
import { LINE_STRING } from 'client/utils/constants/geometryType';

const { DRAWING, MODIFYING } = mapModes;

/**
 * Hook that tracks the current draw line geometry to provide:
 * - The total line distance in metres (including cursor segment during draw)
 * - The active (last-placed) point coordinate
 * - The ability to select a different active point
 * - Visibility state (shown during both drawing and modifying a line)
 *
 * During active drawing the sketch feature lives on the Draw interaction's
 * internal overlay, NOT on the draw VectorSource.  We therefore locate the
 * OL Draw interaction via `map.getInteractions()` and listen for its
 * `drawstart` event which gives us the live sketch feature.
 *
 * During modify mode the feature lives on the draw VectorSource, so we
 * read directly from it and listen for geometry changes.
 *
 * @returns {{
 *   isVisible: boolean,
 *   isModifying: boolean,
 *   distance: number,
 *   activePointCoord: import('ol/coordinate').Coordinate|null,
 *   activePointIndex: number|null,
 *   coordinates: Array,
 *   setActivePointIndex: Function,
 *   deleteActivePoint: Function,
 *   finishDrawing: Function
 * }}
 */
const useDrawLineTracking = () => {
    const { map } = useContext(MapContext);
    const mapMode = useSelector(state => state.map?.mode);
    const drawType = useSelector(state => state.draw?.type);

    const [distance, setDistance] = useState(0);
    const [coordinates, setCoordinates] = useState([]);
    const [activePointIndex, setActivePointIndex] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    const sketchFeatureRef = useRef(null);
    const animFrameRef = useRef(null);
    const geometryListenerRef = useRef(null);
    // Tracks whether the current feature comes from an OL Draw interaction sketch
    const isSketchFromDrawRef = useRef(false);
    // Reference to the OL Draw interaction for finishDrawing()
    const drawInteractionRef = useRef(null);

    const isDrawingLine = mapMode?.includes(DRAWING) && drawType === LINE;
    const isModifyingLine = mapMode?.includes(MODIFYING) && drawType === LINE;
    const isActive = isDrawingLine || isModifyingLine;

    /**
     * Read the sketch feature's geometry and update state.
     * During active Draw, the last coordinate is the cursor (excluded from placed coords).
     * During Modify, all coordinates are placed points.
     */
    const updateFromSketch = useCallback(() => {
        const feature = sketchFeatureRef.current;
        if (!feature) {
            return;
        }

        const geometry = feature.getGeometry?.();
        if (!geometry || geometry.getType?.() !== LINE_STRING) {
            return;
        }

        const coords = geometry.getCoordinates();
        if (!coords || coords.length === 0) {
            return;
        }

        // During active Draw sketch, OL appends the cursor as the last coordinate.
        // During Modify, all coordinates are real placed points.
        const isActiveSketch = isSketchFromDrawRef.current;
        const placedCoords = isActiveSketch && coords.length > 1
            ? coords.slice(0, -1)
            : coords;

        setCoordinates(placedCoords);
        // Use the geometry's planar length (coordinates are projected metres)
        setDistance(geometry.getLength());

        // Always track the last placed point as the active point
        setActivePointIndex(placedCoords.length - 1);
    }, []);

    useEffect(() => {
        if (!map || !isActive) {
            setIsVisible(false);
            setDistance(0);
            setCoordinates([]);
            setActivePointIndex(null);
            sketchFeatureRef.current = null;
            isSketchFromDrawRef.current = false;
            drawInteractionRef.current = null;
            return;
        }

        setIsVisible(true);

        let drawStartKey = null;
        let drawEndKey = null;
        let interactionAddKey = null;
        let drawSourceChangeKey = null;

        const scheduleUpdate = () => {
            if (animFrameRef.current) {
                cancelAnimationFrame(animFrameRef.current);
            }
            animFrameRef.current = requestAnimationFrame(updateFromSketch);
        };

        // === Draw interaction tracking (initial drawing) ===
        const onDrawStart = (evt) => {
            sketchFeatureRef.current = evt.feature;
            isSketchFromDrawRef.current = true;

            if (geometryListenerRef.current) {
                unByKey(geometryListenerRef.current);
            }

            const geometry = evt.feature?.getGeometry?.();
            if (geometry) {
                geometryListenerRef.current = geometry.on('change', scheduleUpdate);
            }

            scheduleUpdate();
        };

        const onDrawEnd = () => {
            if (geometryListenerRef.current) {
                unByKey(geometryListenerRef.current);
                geometryListenerRef.current = null;
            }
            sketchFeatureRef.current = null;
            isSketchFromDrawRef.current = false;
        };

        const attachToInteraction = (interaction) => {
            drawInteractionRef.current = interaction;
            drawStartKey = interaction.on('drawstart', onDrawStart);
            drawEndKey = interaction.on('drawend', onDrawEnd);
        };

        // Locate any existing OL Draw interaction on the map
        const interactions = map.getInteractions();
        const existing = interactions.getArray().find(i => typeof i.finishDrawing === 'function');
        if (existing) {
            attachToInteraction(existing);
        }

        // The Draw interaction may be added after this hook runs
        interactionAddKey = interactions.on('add', (evt) => {
            if (!drawInteractionRef.current && typeof evt.element?.finishDrawing === 'function') {
                attachToInteraction(evt.element);
            }
        });

        // === Modify mode tracking (editing completed line) ===
        if (isModifyingLine) {
            const drawSource = map.getLayerById('draw')?.getSource();
            if (drawSource) {
                const setupFromSource = () => {
                    // Don't override an active Draw sketch feature
                    if (isSketchFromDrawRef.current) {
                        return;
                    }
                    const features = drawSource.getFeatures();
                    if (features?.length > 0) {
                        sketchFeatureRef.current = features[0];
                        isSketchFromDrawRef.current = false;

                        if (geometryListenerRef.current) {
                            unByKey(geometryListenerRef.current);
                        }
                        const geometry = features[0].getGeometry?.();
                        if (geometry) {
                            geometryListenerRef.current = geometry.on('change', scheduleUpdate);
                        }
                        scheduleUpdate();
                    }
                };
                setupFromSource();
                drawSourceChangeKey = drawSource.on('change', setupFromSource);
            }
        }

        // Sync after pan / zoom completes (geometry changes during draw
        // are already captured by the geometry 'change' listener above)
        const moveEndKey = map.on('moveend', () => {
            if (sketchFeatureRef.current) {
                scheduleUpdate();
            }
        });

        return () => {
            if (drawStartKey) { unByKey(drawStartKey); }
            if (drawEndKey) { unByKey(drawEndKey); }
            if (interactionAddKey) { unByKey(interactionAddKey); }
            if (drawSourceChangeKey) { unByKey(drawSourceChangeKey); }
            unByKey(moveEndKey);
            if (geometryListenerRef.current) {
                unByKey(geometryListenerRef.current);
                geometryListenerRef.current = null;
            }
            if (animFrameRef.current) {
                cancelAnimationFrame(animFrameRef.current);
                animFrameRef.current = null;
            }
            drawInteractionRef.current = null;
        };
    }, [map, isActive, isModifyingLine, updateFromSketch]);

    const activePointCoord = (coordinates.length > 0 && activePointIndex !== null && activePointIndex < coordinates.length)
        ? coordinates[activePointIndex]
        : null;

    /**
     * Delete the last placed point from the sketch geometry.
     * During active Draw, uses the OL Draw interaction's removeLastPoint() API
     * so the internal state stays in sync. During Modify, edits the geometry directly.
     */
    const deleteActivePoint = useCallback(() => {
        if (coordinates.length <= 1) {
            return;
        }

        // During active Draw sketch, use the interaction API
        if (isSketchFromDrawRef.current && drawInteractionRef.current) {
            drawInteractionRef.current.removeLastPoint();
            return;
        }

        // During Modify mode, edit the geometry directly
        const feature = sketchFeatureRef.current;
        if (!feature || activePointIndex === null) {
            return;
        }

        const geometry = feature.getGeometry?.();
        if (!geometry || geometry.getType?.() !== LINE_STRING) {
            return;
        }

        const allCoords = geometry.getCoordinates();
        const newCoords = allCoords.filter((_, i) => i !== activePointIndex);

        if (newCoords.length < 1) {
            return;
        }

        geometry.setCoordinates(newCoords);

        // Move active to previous point or 0
        const newIndex = Math.min(activePointIndex, newCoords.length - 2);
        setActivePointIndex(Math.max(0, newIndex));
    }, [activePointIndex, coordinates]);

    /**
     * Programmatically finish the active Draw interaction.
     * Triggers the OL drawend event which DrawAndModify handles (AOI transfer, mode changes).
     */
    const finishDrawing = useCallback(() => {
        if (drawInteractionRef.current) {
            drawInteractionRef.current.finishDrawing();
        }
    }, []);

    return {
        isVisible,
        isModifying: isModifyingLine && !isDrawingLine,
        distance,
        activePointCoord,
        activePointIndex,
        coordinates,
        setActivePointIndex,
        deleteActivePoint,
        finishDrawing
    };
};

export default useDrawLineTracking;
